import React, { useState, useEffect, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Badge, 
  Modal, 
  Alert, 
  Spinner, 
  Toast, 
  ToastContainer,
  ListGroup,
  Table,
  InputGroup,
  Tabs,
  Tab,
  ProgressBar,
  Tooltip,
  OverlayTrigger
} from "react-bootstrap";
import axios from "axios";
import {
  Building,
  ExclamationCircle,
  Shield,
  Key,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Pencil,
  Save,
  X,
  People,
  GeoAlt,
  Phone,
  Envelope,
  Calendar,
  ClockHistory,
  Download,
  Eye,
  InfoCircle,
  ExclamationTriangle,
  ChevronRight,
  ChevronDown,
  Trash,
  Search,
  Plus,
  Globe2,
  Link as LinkIcon,
  Wallet,
  Briefcase,
  PersonCheck,
  Activity,
  Lock,
  Unlock,
  Star,
  StarFill,
  GraphUp,
  ClipboardCheck,
  Globe,
  Telephone,
  EnvelopePaper,
  PinMap,
  CreditCard,
  Bank,
  BoxArrowRight,
  Copy,
  PersonPlus,
  FileText,
  Person,
  Check
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";

export default function EntiteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // États principaux
  const [entite, setEntite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // États pour les rôles (pour l'admin)
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [assigningRoles, setAssigningRoles] = useState(false);
  const [removingRole, setRemovingRole] = useState(false);
  const [roleToRemove, setRoleToRemove] = useState(null);
  const [showRemoveRoleModal, setShowRemoveRoleModal] = useState(false);
  
  // États pour la pagination et recherche des rôles
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [roleItemsPerPage, setRoleItemsPerPage] = useState(5);
  
  // États pour les formulaires
  const [editMode, setEditMode] = useState(false);
  const [entiteData, setEntiteData] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    type: 'commune',
    statut: 'active'
  });
  
  // États pour l'admin (utilisateur associé)
  const [adminUser, setAdminUser] = useState(null);
  const [editAdminMode, setEditAdminMode] = useState(false);
  const [adminData, setAdminData] = useState({
    admin_lname: '',
    admin_fname: '',
    email: '',
    admin_phone: '',
    admin_fonction: '',
    is_active: 1
  });
  
  // Nouveaux états pour la gestion de tous les admins
  const [entiteAdmins, setEntiteAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showAdminEditModal, setShowAdminEditModal] = useState(false);
  
  // États pour les modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // États pour les permissions
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // États pour les notifications
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  
  // État pour le mot de passe réinitialisé
  const [passwordResetData, setPasswordResetData] = useState({
    show: false,
    password: '',
    username: '',
    message: '',
    phone: '',
    sms_sent: undefined,
    sms_error: '',
    sms_response: null,
    user_id: ''
  });
  
  const [togglingStatus, setTogglingStatus] = useState(false);

  // États pour la création d'admin
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    admin_lname: '',
    admin_fname: '',
    admin_phone: '',
    admin_fonction: '',
    email: '',
    admin_type: 'admin_entite',
    matricule_interne: '',
    code_agent_interne: '',
    id_marche: null
  });
  const [savingNewAdmin, setSavingNewAdmin] = useState(false);
  const [newAdminCredentials, setNewAdminCredentials] = useState(null);
  
  // Nouvel état pour la modale des identifiants
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);

  const getAuthToken = () => sessionStorage.getItem("authToken");

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }, []);

  const hideAlert = useCallback(() => setAlertMessage({ show: false, message: '', type: 'info' }), []);
  
  const showAlert = useCallback((message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    setTimeout(() => hideAlert(), duration);
  }, [hideAlert]);

  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), duration);
  };

  const getTypeBadge = (type) => {
    const config = {
      commune: { label: "Commune", color: "primary", icon: <Building className="me-1" size={14} /> },
      entreprise: { label: "Entreprise", color: "success", icon: <Briefcase className="me-1" size={14} /> },
      organisation: { label: "Organisation", color: "secondary", icon: <People className="me-1" size={14} /> }
    };
    const cfg = config[type] || { label: type, color: "secondary", icon: <Building className="me-1" size={14} /> };
    return <Badge bg={cfg.color} className="d-inline-flex align-items-center px-3 py-2 rounded-pill">{cfg.icon}{cfg.label}</Badge>;
  };

  const getStatusBadge = (statut) => {
    const config = {
      active: { label: "Active", color: "success", icon: <CheckCircle className="me-1" size={14} /> },
      inactive: { label: "Inactive", color: "secondary", icon: <XCircle className="me-1" size={14} /> },
      suspended: { label: "Suspendue", color: "warning", icon: <ExclamationCircle className="me-1" size={14} /> }
    };
    const cfg = config[statut] || { label: statut, color: "secondary", icon: null };
    return <Badge bg={cfg.color} className="d-inline-flex align-items-center px-3 py-2 rounded-pill">{cfg.icon}{cfg.label}</Badge>;
  };

  const getUserStatusBadge = (isActive) => {
    return isActive === 1 
      ? <Badge bg="success" className="d-inline-flex align-items-center px-3 py-2 rounded-pill"><CheckCircle className="me-1" size={14} />Actif</Badge>
      : <Badge bg="secondary" className="d-inline-flex align-items-center px-3 py-2 rounded-pill"><XCircle className="me-1" size={14} />Inactif</Badge>;
  };

  const getScopeBadge = (scope) => {
    const colors = { global: "danger", local: "primary", super_admin: "dark" };
    return colors[scope] || "secondary";
  };

  const formatGroupName = (group) => {
    const groups = {
      general: "Général", entite: "Entités", tax: "Taxes", category: "Catégories",
      user: "Utilisateurs", usager: "Usagers", taxpayer: "Contribuables",
      reports: "Rapports", role_permission: "Rôles & Permissions", tax_collector: "Collecte de taxes"
    };
    return groups[group] || group.charAt(0).toUpperCase() + group.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifié";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDateSimple = (dateString) => {
    if (!dateString) return "Non spécifié";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Configuration des types d'admin
  const getAdminTypeConfig = () => ({
    admin_entite: {
      label: "Admin Entité",
      description: "Administrateur de l'entité (hors marché)",
      color: "#0d6efd",
      bgLight: "#cfe2ff",
      icon: Building,
      badge: "Entité",
      roleId: 1002
    },
    admin_principal: {
      label: "Admin Principal",
      description: "Administrateur des marchés (ODD/Commune)",
      color: "#dc3545",
      bgLight: "#f8d7da",
      icon: Globe,
      badge: "ODD",
      roleId: 1003
    }
  });

  // Fonction pour obtenir le role_id
  const getRoleIdFromAdminType = (adminType) => {
    switch (adminType) {
      case 'admin_entite':
        return 1002;
      case 'admin_principal':
        return 1003;
      default:
        return 1002;
    }
  };

  // Fonction pour copier le message des identifiants
  const copyAdminCredentialsMessage = (credentials) => {
    const text = `Bonjour ${credentials.admin_fname},\n\nVotre compte administrateur a été créé.\n\nType: ${credentials.admin_type === 'admin_entite' ? 'Admin Entité (1002)' : 'Admin Principal (1003)'}\n\nIdentifiants :\nEmail : ${credentials.email}\nMot de passe : ${credentials.password_temp}\n${credentials.matricule_interne ? `Matricule interne : ${credentials.matricule_interne}\n` : ''}${credentials.code_agent_interne ? `Code agent interne : ${credentials.code_agent_interne}\n` : ''}\nLien : ${window.location.origin}/login\n\nChangez votre mot de passe à la première connexion.`;
    navigator.clipboard.writeText(text);
    showNotification('Message copié ! ✅', 'success');
  };

  // Charger les détails complets de l'admin
  const fetchAdminDetails = useCallback(async (adminUserId) => {
    if (!adminUserId) return null;
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${adminUserId}`, { headers: getHeaders() });
      if (response.data && response.data.code === 200 && response.data.user) {
        return response.data.user;
      }
      return null;
    } catch (error) {
      console.error('Erreur fetchAdminDetails:', error);
      return null;
    }
  }, [getHeaders]);

  // Charger tous les admins de l'entité
  const fetchEntiteAdmins = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoadingAdmins(true);
      const response = await axios.get(`${API_BASE_URL}/entites/${id}/admins`, { 
        headers: getHeaders() 
      });
      
      if (response.data && response.data.code === 200) {
        setEntiteAdmins(response.data.data || []);
        console.log('Admins de l\'entité:', response.data.data);
      } else {
        setEntiteAdmins([]);
      }
    } catch (error) {
      console.error('Erreur fetchEntiteAdmins:', error);
      setEntiteAdmins([]);
      showAlert(`Erreur chargement des admins: ${error.message}`, 'danger');
    } finally {
      setLoadingAdmins(false);
    }
  }, [getHeaders, id]);

  // Charger les rôles disponibles
  const fetchAvailableRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const response = await axios.get(`${API_BASE_URL}/roles`, { headers: getHeaders() });
      
      let rolesData = [];
      if (Array.isArray(response.data)) rolesData = response.data;
      else if (response.data?.data && Array.isArray(response.data.data)) rolesData = response.data.data;
      
      const formattedRoles = rolesData.map(role => ({
        id: role.role_id || role.id,
        role_id: role.role_id,
        name: role.name,
        label: role.name ? role.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '',
        description: role.description || '',
        scope: role.scope || 'local'
      }));
      
      setAvailableRoles(formattedRoles);
    } catch (error) {
      console.error('Erreur fetchAvailableRoles:', error);
    } finally {
      setLoadingRoles(false);
    }
  }, [getHeaders]);

  // Charger les détails de l'entité
  const fetchEntiteDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/entites/${id}`, { headers: getHeaders() });
      
      if (response.data && response.data.code === 200 && response.data.data) {
        const entiteData = response.data.data;
        
        setEntite(entiteData);
        setEntiteData({
          nom: entiteData.nom || '',
          adresse: entiteData.adresse || '',
          telephone: entiteData.telephone || '',
          email: entiteData.email || '',
          type: entiteData.type || 'commune',
          statut: entiteData.blocked === 'Active' ? 'active' : 
                   entiteData.blocked === 'Inactive' ? 'inactive' : 
                   entiteData.deleted_at ? 'inactive' : 'active'
        });
        
        // Charger les détails complets de l'admin si admin_user_id existe
        if (entiteData.admin_user_id) {
          const adminDetails = await fetchAdminDetails(entiteData.admin_user_id);
          if (adminDetails) {
            setAdminUser(adminDetails);
            setAdminData({
              admin_lname: adminDetails.admin_lname || adminDetails.last_name || '',
              admin_fname: adminDetails.admin_fname || adminDetails.first_name || '',
              email: adminDetails.email || '',
              admin_phone: adminDetails.admin_phone || adminDetails.phone || '',
              admin_fonction: adminDetails.admin_fonction || adminDetails.fonction || '',
              is_active: adminDetails.is_active !== undefined ? adminDetails.is_active : 1
            });
            
            // Charger les rôles de l'admin
            if (adminDetails.roles && Array.isArray(adminDetails.roles)) {
              setUserRoles(adminDetails.roles);
            } else if (adminDetails.role) {
              setUserRoles([adminDetails.role]);
            } else {
              // Essayer de charger les rôles séparément
              try {
                const rolesResponse = await axios.get(`${API_BASE_URL}/users/${entiteData.admin_user_id}/roles`, { headers: getHeaders() });
                if (rolesResponse.data && rolesResponse.data.data) {
                  setUserRoles(rolesResponse.data.data);
                }
              } catch (err) {
                console.error("Erreur chargement rôles admin:", err);
              }
            }
          }
        } else {
          setAdminUser(null);
          setUserRoles([]);
        }
      } else {
        throw new Error('Format de données invalide');
      }
    } catch (error) {
      console.error('Erreur fetchEntiteDetails:', error);
      setError(`Erreur de chargement: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [getHeaders, id, fetchAdminDetails]);

  // Charger les rôles d'un utilisateur spécifique
  const fetchUserRoles = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}/roles`, { headers: getHeaders() });
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error("Erreur chargement rôles:", err);
      return [];
    }
  }, [getHeaders]);

  // Charger les permissions d'un rôle
  const fetchRolePermissionsFromApi = useCallback(async (roleId) => {
    if (!roleId) return [];
    try {
      const response = await axios.get(`${API_BASE_URL}/roles/${roleId}/permissions`, { headers: getHeaders() });
      let permissionsList = [];
      if (response.data?.permissions && Array.isArray(response.data.permissions)) {
        permissionsList = response.data.permissions;
      } else if (Array.isArray(response.data)) {
        permissionsList = response.data;
      }
      return permissionsList.map(p => ({
        id: p.permission_id || p.id,
        name: p.name,
        display_name: p.name?.replace(/:/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '',
        description: p.description || '',
        group: p.name?.split(':')[0] || 'general',
        action: p.name?.split(':')[1] || 'access',
        scope: p.scope || 'local'
      }));
    } catch (error) {
      console.error(`Erreur fetch permissions rôle ${roleId}:`, error);
      return [];
    }
  }, [getHeaders]);

  // Charger toutes les permissions de l'admin
  const fetchPermissions = useCallback(async () => {
    if (!userRoles.length) {
      setPermissions([]);
      setGroupedPermissions({});
      return;
    }
    
    try {
      setLoadingPermissions(true);
      let allPermissions = [];
      for (const role of userRoles) {
        const roleId = role.role_id || role.id;
        const perms = await fetchRolePermissionsFromApi(roleId);
        allPermissions = [...allPermissions, ...perms];
      }
      
      const uniquePermissions = Array.from(new Map(allPermissions.map(p => [p.id, p])).values());
      setPermissions(uniquePermissions);
      
      const grouped = uniquePermissions.reduce((acc, perm) => {
        const group = perm.group || 'general';
        if (!acc[group]) acc[group] = [];
        acc[group].push(perm);
        return acc;
      }, {});
      setGroupedPermissions(grouped);
      setExpandedGroups(Object.keys(grouped).reduce((acc, g) => ({ ...acc, [g]: true }), {}));
    } catch (error) {
      console.error('Erreur fetchPermissions:', error);
    } finally {
      setLoadingPermissions(false);
    }
  }, [userRoles, fetchRolePermissionsFromApi]);

  // Filtrer les rôles disponibles
  const getFilteredAvailableRoles = () => {
    let filtered = [...availableRoles];
    if (roleSearchTerm) {
      filtered = filtered.filter(r => 
        r.label.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
        (r.description && r.description.toLowerCase().includes(roleSearchTerm.toLowerCase()))
      );
    }
    return filtered.filter(r => !userRoles.some(ur => (ur.role_id || ur.id) === r.role_id));
  };

  const getPaginatedRoles = () => {
    const filtered = getFilteredAvailableRoles();
    const start = (roleCurrentPage - 1) * roleItemsPerPage;
    return {
      items: filtered.slice(start, start + roleItemsPerPage),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / roleItemsPerPage)
    };
  };

  const changeRolePage = (page) => setRoleCurrentPage(page);
  const changeRoleItemsPerPage = (e) => {
    setRoleItemsPerPage(parseInt(e.target.value));
    setRoleCurrentPage(1);
  };
  const toggleSelectAllRoles = (select) => {
    const currentIds = getPaginatedRoles().items.map(r => r.role_id);
    if (select) {
      setSelectedRoles(prev => [...new Set([...prev, ...currentIds])]);
    } else {
      setSelectedRoles(prev => prev.filter(id => !currentIds.includes(id)));
    }
  };

  // Filtrer les admins : UNIQUEMENT 1002 et 1003
  const filteredAdmins = entiteAdmins.filter(admin => {
    const roleId = admin.role_id || admin.role?.role_id || null;
    return roleId === 1002 || roleId === 1003;
  });

  // Assigner des rôles à l'admin
  const handleAssignRoles = async () => {
    if (!adminUser || selectedRoles.length === 0) {
      showAlert("Veuillez sélectionner au moins un rôle", 'warning');
      return;
    }

    setAssigningRoles(true);

    try {
      const payload = { role_ids: selectedRoles };
      const userId = adminUser.user_id || adminUser.id;
      
      await axios.post(
        `${API_BASE_URL}/users/${userId}/roles`,
        payload,
        { headers: getHeaders() }
      );
      
      showNotification(`${selectedRoles.length} rôle(s) assigné(s) avec succès`, 'success');
      setSelectedRoles([]);
      setRoleSearchTerm('');
      setRoleCurrentPage(1);
      await fetchEntiteDetails();
      await fetchEntiteAdmins();
      await fetchPermissions();
    } catch (error) {
      console.error('Erreur assignation des rôles:', error);
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setAssigningRoles(false);
    }
  };

  // Assigner des rôles à un admin spécifique
  const handleAssignRolesToAdmin = async (admin, roleIds) => {
    if (!admin || !roleIds.length) {
      showAlert("Veuillez sélectionner au moins un rôle", 'warning');
      return;
    }

    setAssigningRoles(true);
    try {
      const payload = { role_ids: roleIds };
      const userId = admin.user_id || admin.id;
      
      await axios.post(
        `${API_BASE_URL}/users/${userId}/roles`,
        payload,
        { headers: getHeaders() }
      );
      
      showNotification(`${roleIds.length} rôle(s) assigné(s) avec succès à ${admin.admin_lname || admin.last_name}`, 'success');
      setSelectedRoles([]);
      await fetchEntiteAdmins();
    } catch (error) {
      console.error('Erreur assignation des rôles:', error);
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setAssigningRoles(false);
    }
  };

  // Retirer un rôle de l'admin
  const handleRemoveRole = async () => {
    if (!adminUser || !roleToRemove) return;
    setRemovingRole(true);
    try {
      const userId = adminUser.user_id || adminUser.id;
      await axios.delete(`${API_BASE_URL}/users/${userId}/roles`, {
        headers: getHeaders(),
        data: { role_ids: [roleToRemove.role_id] }
      });
      showNotification('Rôle retiré avec succès', 'success');
      setShowRemoveRoleModal(false);
      setRoleToRemove(null);
      await fetchEntiteDetails();
      await fetchEntiteAdmins();
      await fetchPermissions();
    } catch (error) {
      showAlert(`Erreur: ${error.message}`, 'danger');
    } finally {
      setRemovingRole(false);
    }
  };

  // Sauvegarder les modifications de l'entité
  const handleSaveEntite = async () => {
    if (!entiteData.nom.trim()) {
      showAlert("Veuillez remplir le nom de l'entité", 'warning');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/entites/${entite.entite_id}`, {
        nom: entiteData.nom.trim(),
        adresse: entiteData.adresse,
        telephone: entiteData.telephone,
        email: entiteData.email,
        type: entiteData.type
      }, { headers: getHeaders() });
      
      setEditMode(false);
      await fetchEntiteDetails();
      showNotification('Entité mise à jour avec succès', 'success');
    } catch (error) {
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarder les modifications de l'admin
  const handleSaveAdmin = async () => {
    if (!adminData.email.trim() || !adminData.admin_lname.trim() || !adminData.admin_fname.trim()) {
      showAlert("Veuillez remplir les champs obligatoires", 'warning');
      return;
    }

    setSaving(true);
    try {
      const userId = adminUser.user_id || adminUser.id;
      await axios.put(`${API_BASE_URL}/users/${userId}`, {
        email: adminData.email.trim(),
        last_name: adminData.admin_lname.trim(),
        first_name: adminData.admin_fname.trim(),
        phone: adminData.admin_phone.trim(),
        fonction: adminData.admin_fonction.trim(),
      }, { headers: getHeaders() });
      
      setEditAdminMode(false);
      await fetchEntiteDetails();
      await fetchEntiteAdmins();
      showNotification('Admin mis à jour avec succès', 'success');
    } catch (error) {
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarder les modifications d'un admin spécifique
  const handleUpdateSpecificAdmin = async () => {
    if (!selectedAdmin) return;
    
    if (!adminData.email.trim() || !adminData.admin_lname.trim() || !adminData.admin_fname.trim()) {
      showAlert("Veuillez remplir les champs obligatoires", 'warning');
      return;
    }

    setSaving(true);
    try {
      const userId = selectedAdmin.user_id || selectedAdmin.id;
      await axios.put(`${API_BASE_URL}/users/${userId}`, {
        email: adminData.email.trim(),
        last_name: adminData.admin_lname.trim(),
        first_name: adminData.admin_fname.trim(),
        phone: adminData.admin_phone.trim(),
        fonction: adminData.admin_fonction.trim(),
        entite_id: entite?.entite_id || id
      }, { headers: getHeaders() });
      
      setShowAdminEditModal(false);
      setSelectedAdmin(null);
      await fetchEntiteAdmins();
      if (adminUser && (adminUser.user_id === userId || adminUser.id === userId)) {
        await fetchEntiteDetails();
      }
      showNotification('Admin mis à jour avec succès', 'success');
    } catch (error) {
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Changer le statut de l'admin principal
  const handleToggleAdminStatus = async () => {
    if (!adminUser) return;
    setTogglingStatus(true);
    const newStatus = adminUser.is_active === 1 ? 0 : 1;
    try {
      const userId = adminUser.user_id || adminUser.id;
      await axios.put(`${API_BASE_URL}/users/${userId}`, { is_active: newStatus,entite_id: entite?.entite_id || id }, { headers: getHeaders() });
      setShowStatusModal(false);
      showNotification(`Admin ${newStatus === 1 ? 'activé' : 'désactivé'}`, 'success');
      await fetchEntiteDetails();
      await fetchEntiteAdmins();
    } catch (error) {
      showAlert(`Erreur: ${error.message}`, 'danger');
    } finally {
      setTogglingStatus(false);
    }
  };

  // Changer le statut d'un admin spécifique
  const handleToggleSpecificAdminStatus = async () => {
    if (!selectedAdmin) return;
    setTogglingStatus(true);
    const newStatus = selectedAdmin.is_active === 1 ? 0 : 1;
    try {
      const userId = selectedAdmin.user_id || selectedAdmin.id;
      await axios.put(`${API_BASE_URL}/users/${userId}`, { is_active: newStatus,entite_id: entite?.entite_id || id }, { headers: getHeaders() });
      setShowStatusModal(false);
      showNotification(`Admin ${newStatus === 1 ? 'activé' : 'désactivé'} avec succès`, 'success');
      await fetchEntiteAdmins();
      if (adminUser && (adminUser.user_id === userId || adminUser.id === userId)) {
        await fetchEntiteDetails();
      }
    } catch (error) {
      showAlert(`Erreur: ${error.message}`, 'danger');
    } finally {
      setTogglingStatus(false);
    }
  };

  // Réinitialiser le mot de passe de l'admin principal
  const handleResetPassword = async () => {
    setResettingPassword(true);
    try {
      const userId = adminUser.user_id || adminUser.id;
      const response = await axios.post(
        `${API_BASE_URL}/password/reset-admin-pwd`,
        { user_id: userId },
        { headers: getHeaders() }
      );
      
      setShowResetPasswordModal(false);
      
      if (response.data && response.data.code === 200) {
        setPasswordResetData({
          show: true,
          password: response.data.password_show,
          username: response.data.username,
          phone: response.data.phone_without_code || adminUser?.admin_phone || '',
          message: response.data.message || 'Le nouveau mot de passe a été généré avec succès.',
          sms_sent: response.data.sms_sent,
          sms_error: response.data.sms_error || '',
          sms_response: response.data.sms_response || null,
          user_id: response.data.user_id || userId
        });
      } else {
        showNotification('Mot de passe réinitialisé avec succès.', 'success');
      }
    } catch (error) {
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setResettingPassword(false);
    }
  };

  // Réinitialiser le mot de passe d'un admin spécifique
  const handleResetSpecificAdminPassword = async () => {
    if (!selectedAdmin) return;
    setResettingPassword(true);
    try {
      const userId = selectedAdmin.user_id || selectedAdmin.id;
      const response = await axios.post(
        `${API_BASE_URL}/password/reset-admin-pwd`,
        { user_id: userId },
        { headers: getHeaders() }
      );
      
      setShowResetPasswordModal(false);
      
      if (response.data && response.data.code === 200) {
        setPasswordResetData({
          show: true,
          password: response.data.password_show,
          username: response.data.username,
          phone: response.data.phone_without_code || selectedAdmin?.admin_phone || '',
          message: response.data.message || `Le nouveau mot de passe pour ${selectedAdmin.admin_lname || selectedAdmin.last_name} a été généré avec succès.`,
          sms_sent: response.data.sms_sent,
          sms_error: response.data.sms_error || '',
          sms_response: response.data.sms_response || null,
          user_id: response.data.user_id || userId
        });
      } else {
        showNotification('Mot de passe réinitialisé avec succès.', 'success');
      }
    } catch (error) {
      showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setResettingPassword(false);
    }
  };

  // Voir les permissions d'un admin spécifique
  const handleViewPermissions = async (admin) => {
    setSelectedAdmin(admin);
    const roles = await fetchUserRoles(admin.user_id || admin.id);
    setUserRoles(roles);
    setShowPermissionsModal(true);
  };

  const handleShowPermissions = () => { 
    fetchPermissions(); 
    setShowPermissionsModal(true); 
  };
  
  const handleEditToggle = () => { 
    if (editMode) fetchEntiteDetails(); 
    setEditMode(!editMode); 
  };
  
  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const exportToCSV = (data, filename) => {
    if (!data?.length) { showAlert("Aucune donnée à exporter", 'warning'); return; }
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(v => typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v).join(','));
    const blob = new Blob([headers, ...rows].join('\n'), { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    showNotification('Export CSV réussi', 'success');
  };

  // Fonction pour sauvegarder le nouvel admin
  const handleSaveNewAdmin = async () => {
    // Validation
    if (!newAdminData.admin_lname.trim() || !newAdminData.admin_fname.trim() || 
        !newAdminData.admin_phone.trim() || !newAdminData.admin_fonction.trim()) {
      showAlert("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    setSavingNewAdmin(true);
    
    const roleId = getRoleIdFromAdminType(newAdminData.admin_type);
    
    const adminPayload = {
      full_name: newAdminData.admin_lname.trim(),
      first_name: newAdminData.admin_fname.trim(),
      fonction: newAdminData.admin_fonction.trim(),
      telephone: newAdminData.admin_phone.trim(),
      email: newAdminData.email.trim(),
      matricule_interne: newAdminData.matricule_interne.trim(),
      code_agent_interne: newAdminData.code_agent_interne.trim(),
      role_id: roleId,
      id_marche: null,
      entite_id: entite?.entite_id || id
    };
    
    try {
      const response = await axios.post(`${API_BASE_URL}/users`, adminPayload, { headers: getHeaders() });
      if (response.data) {
        const passwordTemp = response.data.password_temp || response.data.temp_password;
        
        setNewAdminCredentials({
          admin_fname: newAdminData.admin_fname,
          admin_lname: newAdminData.admin_lname,
          email: response.data.user?.email || newAdminData.email,
          admin_phone: newAdminData.admin_phone,
          password_temp: passwordTemp || 'Non fourni',
          matricule_interne: newAdminData.matricule_interne,
          code_agent_interne: newAdminData.code_agent_interne,
          admin_type: newAdminData.admin_type,
          role_id: roleId
        });
        
        // Fermer la modale d'ajout et ouvrir la modale des identifiants
        setShowAddAdminModal(false);
        setShowCredentialsModal(true);
        
        // Recharger les admins
        await fetchEntiteAdmins();
        await fetchEntiteDetails();
      }
    } catch (error) {
      console.error('Erreur création admin:', error);
      showAlert(error.response?.data?.message || '❌ Erreur lors de la création', 'danger');
    } finally {
      setSavingNewAdmin(false);
    }
  };

  useEffect(() => {
    if (id) { 
      fetchEntiteDetails(); 
      fetchAvailableRoles();
      fetchEntiteAdmins();
    }
  }, [id, fetchEntiteDetails, fetchAvailableRoles, fetchEntiteAdmins]);

  useEffect(() => {
    if (userRoles.length) fetchPermissions();
  }, [userRoles, fetchPermissions]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 text-muted">Chargement des détails de l'entité...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="container-fluid">
            <Alert variant="danger" className="rounded-4 shadow-sm">
              <ExclamationTriangle className="me-2" size={20} />
              {error}
            </Alert>
            <Button variant="primary" onClick={() => navigate('/superAdmin/entites')} className="rounded-pill">
              <ArrowLeft className="me-2" />Retour à la liste
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const displayAdminName = adminUser 
    ? `${adminUser.admin_lname || adminUser.last_name || ''} ${adminUser.admin_fname || adminUser.first_name || ''}`.trim() || adminUser.full_name || adminUser.name || 'Non spécifié' 
    : entite?.admin_name || 'Aucun admin associé';
  
  const displayAdminEmail = adminUser?.email || entite?.admin_email || 'Non spécifié';
  const displayAdminPhone = adminUser?.admin_phone || adminUser?.phone || entite?.admin_phone || 'Non spécifié';

  // Type d'admin config pour l'affichage
  const adminTypeConfig = getAdminTypeConfig();

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4 bg-light">
        <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
          {notifications.map(n => (
            <Toast key={n.id} show={n.show} onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} delay={5000} autohide>
              <Toast.Header className={`bg-${n.type} text-white`}>
                {n.type === 'success' ? <CheckCircle className="me-2" /> : <InfoCircle className="me-2" />}
                <strong className="me-auto">{n.type === 'success' ? 'Succès' : 'Information'}</strong>
              </Toast.Header>
              <Toast.Body>{n.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert variant={alertMessage.type} dismissible onClose={hideAlert} className="shadow-sm border-0 rounded-3">
              <div className="d-flex align-items-center">
                {alertMessage.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
                 alertMessage.type === 'danger' ? <XCircle className="me-2 fs-5" /> :
                 <InfoCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alertMessage.message}</span>
              </div>
            </Alert>
          </div>
        )}

        <div className="container-fluid ">
          {/* Header amélioré */}
          <div className="bg-gradient-primary rounded-3 p-4 mb-4 shadow-sm">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <div>
                <ol className="breadcrumb mb-2">
                  <li className="breadcrumb-item"><Link to="/superAdmin/dashboard" className="text-white-50">Tableau de bord</Link></li>
                  <li className="breadcrumb-item"><Link to="/superAdmin/entites" className="text-white-50">Entités</Link></li>
                  <li className="breadcrumb-item active text-white">Détails</li>
                </ol>
                <div className="d-flex align-items-center flex-wrap gap-3 mt-2">
                  <h1 className="text-white mb-0 fs-2 fw-bold">{entite?.nom}</h1>
                  {entite && getStatusBadge(entiteData.statut)}
                  {entite && getTypeBadge(entiteData.type)}
                </div>
                <div className="d-flex flex-wrap gap-3 mt-2">
                  <span className="text-white small">
                    <LinkIcon size={14} className="me-1" /> ID: {entite?.entite_id}
                  </span>
                  {entite?.code && (
                    <span className="text-white small">
                      <StarFill size={14} className="me-1" /> Code: {entite.code}
                    </span>
                  )}
                  {entite?.country_code && (
                    <span className="text-white small">
                      <Globe size={14} className="me-1" /> Pays: {entite.country_code}
                    </span>
                  )}
                  {entite?.default_currency && (
                    <span className="text-white small">
                      <CreditCard size={14} className="me-1" /> Devise: {entite.default_currency}
                    </span>
                  )}
                </div>
              </div>
              <div className="d-flex gap-2 mt-3 mt-md-0">
                <Button variant="light" onClick={() => navigate('/superAdmin/entites')} className="rounded-pill px-4">
                  <ArrowLeft className="me-2" />Retour
                </Button>
                <Button variant="outline-light" onClick={handleEditToggle} className="rounded-pill px-4">
                  <Pencil className="me-2" />{editMode ? 'Annuler' : 'Modifier'}
                </Button>
              </div>
            </div>
          </div>

          <Tabs defaultActiveKey="entite" className="mb-4 border-0">
            {/* Onglet Entité */}
            <Tab eventKey="entite" title={<><Building className="me-2" />Entité</>}>
              <Row className="g-4 mt-2">
                <Col lg={12}>
                  <Card className="border-0 shadow-sm rounded-4 bg-white">
                    <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                      <Card.Title className="mb-0 fs-5 fw-bold text-primary">
                        <Building className="me-2" />Informations de l'Entité
                      </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-4">
                      {editMode ? (
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">Nom de l'entité <span className="text-danger">*</span></Form.Label>
                              <Form.Control type="text" value={entiteData.nom} onChange={e => setEntiteData({...entiteData, nom: e.target.value})} required className="rounded-3" />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">Type d'entité</Form.Label>
                              <Form.Select value={entiteData.type} onChange={e => setEntiteData({...entiteData, type: e.target.value})} className="rounded-3">
                                <option value="commune">Commune</option>
                                <option value="entreprise">Entreprise</option>
                                <option value="organisation">Organisation</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold"><Telephone className="me-1" size={14} /> Téléphone</Form.Label>
                              <Form.Control type="tel" value={entiteData.telephone} onChange={e => setEntiteData({...entiteData, telephone: e.target.value})} className="rounded-3" />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold"><Envelope className="me-1" size={14} /> Email</Form.Label>
                              <Form.Control type="email" value={entiteData.email} onChange={e => setEntiteData({...entiteData, email: e.target.value})} className="rounded-3" />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold"><GeoAlt className="me-1" size={14} /> Adresse</Form.Label>
                              <Form.Control as="textarea" rows={2} value={entiteData.adresse} onChange={e => setEntiteData({...entiteData, adresse: e.target.value})} className="rounded-3" />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <div className="d-flex justify-content-end gap-2 pt-2">
                              <Button variant="light" onClick={handleEditToggle} className="rounded-pill px-4"><X className="me-2" />Annuler</Button>
                              <Button variant="primary" onClick={handleSaveEntite} disabled={saving} className="rounded-pill px-4">
                                {saving ? <Spinner size="sm" className="me-2" /> : <><Save className="me-2" />Enregistrer</>}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      ) : (
                        <Row>
                          <Col md={6}>
                            <div className="mb-4">
                              <h6 className="text-muted mb-3 text-uppercase small fw-bold">Identité</h6>
                              <div className="bg-light p-3 rounded-3 mb-3">
                                <p className="mb-2"><strong className="text-primary">Nom:</strong> {entite?.nom}</p>
                                <p className="mb-2"><strong className="text-primary">Type:</strong> {getTypeBadge(entiteData.type)}</p>
                                <p className="mb-0"><strong className="text-primary">Code Entité:</strong> <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">{entite?.entite_id || 'N/A'}</Badge></p>
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <h6 className="text-muted mb-3 text-uppercase small fw-bold">Contact</h6>
                              <div className="bg-light p-3 rounded-3">
                                <p className="mb-2"><strong className="text-primary"><Phone className="me-2" size={14} /> Téléphone:</strong> {entite?.telephone || 'Non spécifié'}</p>
                                <p className="mb-2"><strong className="text-primary"><Envelope className="me-2" size={14} /> Email:</strong> {entite?.email || 'Non spécifié'}</p>
                                <p className="mb-0"><strong className="text-primary"><GeoAlt className="me-2" size={14} /> Adresse:</strong> {entite?.adresse || 'Non spécifiée'}</p>
                              </div>
                            </div>
                          </Col>
                          <Col md={12}>
                            <div className="mb-4">
                              <h6 className="text-muted mb-3 text-uppercase small fw-bold">Configuration</h6>
                              <div className="bg-light p-3 rounded-3">
                                <p className="mb-0"><strong className="text-primary"><Globe className="me-2" size={14} /> Pays / Devise:</strong> {entite?.country_code || 'SN'} / {entite?.default_currency || 'XOF'}</p>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            {/* Onglet Admin Principal */}
            <Tab eventKey="admin" title={<><PersonCheck className="me-2" />Admin Principal</>}>
              {adminUser || entite?.admin_user_id ? (
                <Row className="g-4 mt-2">
                  <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 bg-white">
                      <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                        <Card.Title className="mb-0 fs-5 fw-bold text-success">
                          <PersonCheck className="me-2" />Informations de l'Administrateur Principal
                        </Card.Title>
                      </Card.Header>
                      <Card.Body className="p-4 bg-white">
                        {editAdminMode ? (
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="text" value={adminData.admin_lname} onChange={e => setAdminData({...adminData, admin_lname: e.target.value})} required className="rounded-3" />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="text" value={adminData.admin_fname} onChange={e => setAdminData({...adminData, admin_fname: e.target.value})} required className="rounded-3" />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold"><Envelope className="me-1" size={14} /> Email <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="email" value={adminData.email} onChange={e => setAdminData({...adminData, email: e.target.value})} required className="rounded-3" />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold"><Phone className="me-1" size={14} /> Téléphone</Form.Label>
                                <Form.Control type="tel" value={adminData.admin_phone} onChange={e => setAdminData({...adminData, admin_phone: e.target.value})} className="rounded-3" />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold"><Briefcase className="me-1" size={14} /> Fonction</Form.Label>
                                <Form.Control type="text" value={adminData.admin_fonction} onChange={e => setAdminData({...adminData, admin_fonction: e.target.value})} className="rounded-3" />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold"><Shield className="me-1" size={14} /> Statut</Form.Label>
                                <Form.Select value={adminData.is_active} onChange={e => setAdminData({...adminData, is_active: parseInt(e.target.value)})} className="rounded-3">
                                  <option value={1}>Actif</option>
                                  <option value={0}>Inactif</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <div className="d-flex justify-content-end gap-2 pt-2">
                                <Button variant="light" onClick={() => setEditAdminMode(false)} className="rounded-pill px-4"><X className="me-2" />Annuler</Button>
                                <Button variant="success" onClick={handleSaveAdmin} disabled={saving} className="rounded-pill px-4">
                                  {saving ? <Spinner size="sm" className="me-2" /> : <><Save className="me-2" />Enregistrer</>}
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        ) : (
                          <Row>
                            <Col md={6}>
                              <div className="mb-4">
                                <h6 className="text-muted mb-3 text-uppercase small fw-bold">Identité</h6>
                                <div className="bg-light p-3 rounded-3">
                                  <p className="mb-2"><strong className="text-success">Nom complet:</strong> {displayAdminName}</p>
                                  <p className="mb-2"><strong className="text-success"><Envelope className="me-1" size={14} /> Email:</strong> {displayAdminEmail}</p>
                                  <p className="mb-2"><strong className="text-success"><Phone className="me-1" size={14} /> Téléphone:</strong> {displayAdminPhone}</p>
                                  <p className="mb-0"><strong className="text-success"><Briefcase className="me-1" size={14} /> Fonction:</strong> {adminUser?.admin_fonction || adminUser?.fonction || 'Administrateur'}</p>
                                </div>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="mb-4">
                                <h6 className="text-muted mb-3 text-uppercase small fw-bold">Statut</h6>
                                <div className="bg-light p-3 rounded-3">
                                  <p className="mb-2"><strong className="text-success">Statut:</strong> {getUserStatusBadge(adminUser?.is_active !== undefined ? adminUser.is_active : 1)}</p>
                                  <p className="mb-2"><strong className="text-success">Dernière connexion:</strong> {adminUser?.last_login_at ? formatDate(adminUser.last_login_at) : "Jamais"}</p>
                                  <p className="mb-0"><strong className="text-success"><Calendar className="me-1" size={14} /> Créé le:</strong> {formatDate(adminUser?.created_at || entite?.created_at)}</p>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>

                    {/* Gestion des Rôles améliorée */}
                    <Card className="border-0 shadow-sm rounded-4 mt-4 bg-white">
                      <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                        <Card.Title className="mb-0 fs-5 fw-bold text-warning">
                          <Shield className="me-2" />Gestion des Rôles
                        </Card.Title>
                      </Card.Header>
                      <Card.Body className="p-4">
                        <div className="mb-4">
                          <h6 className="text-muted mb-3">Rôles actuels ({userRoles.length})</h6>
                          {userRoles.length === 0 ? (
                            <Alert variant="info" className="py-3 rounded-3"><InfoCircle className="me-2" />Aucun rôle assigné.</Alert>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              {userRoles.map(role => (
                                <Badge key={role.role_id || role.id} bg={getScopeBadge(role.scope)} className="d-inline-flex align-items-center px-3 py-2 rounded-pill" style={{ fontSize: '0.9rem' }}>
                                  <Shield className="me-2" size={14} />
                                  {role.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || role.role_name || 'Rôle'}
                                  <Button variant="link" size="sm" className="text-white p-0 ms-2" onClick={() => { setRoleToRemove({ role_id: role.role_id || role.id, name: role.name || role.role_name }); setShowRemoveRoleModal(true); }}><X size={14} /></Button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <h6 className="text-muted mb-3">Assigner de nouveaux rôles</h6>
                          {loadingRoles ? <Spinner size="sm" /> : getFilteredAvailableRoles().length === 0 ? (
                            <Alert variant="info" className="py-3 rounded-3">Tous les rôles sont déjà assignés.</Alert>
                          ) : (
                            <>
                              <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
                                <div className="d-flex gap-2">
                                  <Button size="sm" variant="outline-primary" onClick={() => toggleSelectAllRoles(true)} className="rounded-pill"><CheckCircle className="me-1" size={14} />Tout sélectionner</Button>
                                  <Button size="sm" variant="outline-secondary" onClick={() => toggleSelectAllRoles(false)} className="rounded-pill"><XCircle className="me-1" size={14} />Tout désélectionner</Button>
                                </div>
                                <div className="d-flex gap-2">
                                  <InputGroup style={{ width: '250px' }}>
                                    <InputGroup.Text className="bg-white"><Search size={14} /></InputGroup.Text>
                                    <Form.Control type="text" placeholder="Rechercher..." value={roleSearchTerm} onChange={e => { setRoleSearchTerm(e.target.value); setRoleCurrentPage(1); }} size="sm" className="rounded-3" />
                                  </InputGroup>
                                  <Form.Select size="sm" value={roleItemsPerPage} onChange={changeRoleItemsPerPage} style={{ width: 'auto' }} className="rounded-3">
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                  </Form.Select>
                                </div>
                              </div>
                              <div className="table-responsive">
                                <Table hover size="sm" className="mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th style={{ width: '40px' }}><Form.Check checked={getPaginatedRoles().items.length > 0 && getPaginatedRoles().items.every(r => selectedRoles.includes(r.role_id))} onChange={e => toggleSelectAllRoles(e.target.checked)} /></th>
                                      <th>Nom du rôle</th>
                                      <th>Description</th>
                                      <th>Scope</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedRoles().items.map(role => (
                                      <tr key={role.role_id}>
                                        <td><Form.Check checked={selectedRoles.includes(role.role_id)} onChange={e => e.target.checked ? setSelectedRoles([...selectedRoles, role.role_id]) : setSelectedRoles(selectedRoles.filter(id => id !== role.role_id))} /></td>
                                        <td><strong>{role.label}</strong><br /><small className="text-muted">{role.name}</small></td>
                                        <td><small>{role.description || '-'}</small></td>
                                        <td><Badge bg={getScopeBadge(role.scope)} className="rounded-pill">{role.scope}</Badge></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                              {getPaginatedRoles().totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <small className="text-muted">{((roleCurrentPage-1)*roleItemsPerPage)+1} à {Math.min(roleCurrentPage*roleItemsPerPage, getPaginatedRoles().total)} sur {getPaginatedRoles().total}</small>
                                  <nav>
                                    <ul className="pagination pagination-sm mb-0">
                                      {[...Array(getPaginatedRoles().totalPages)].map((_, i) => (
                                        <li key={i} className={`page-item ${roleCurrentPage === i+1 ? 'active' : ''}`}>
                                          <button className="page-link rounded-3" onClick={() => changeRolePage(i+1)}>{i+1}</button>
                                        </li>
                                      ))}
                                    </ul>
                                  </nav>
                                </div>
                              )}
                              <div className="mt-3 pt-2 border-top d-flex justify-content-end">
                                <Button variant="warning" onClick={handleAssignRoles} disabled={assigningRoles || selectedRoles.length === 0} className="rounded-pill px-4">
                                  {assigningRoles ? <Spinner size="sm" className="me-2" /> : <Plus className="me-2" size={14} />}
                                  Assigner {selectedRoles.length} rôle(s)
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4 bg-white">
                      <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                        <Card.Title className="mb-0 fs-5 fw-bold text-info">
                          <Key className="me-2" />Actions Rapides
                        </Card.Title>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <ListGroup variant="flush">
                          <ListGroup.Item className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div><Key className="me-2 text-info" size={18} />Réinitialiser le mot de passe</div>
                            <Button size="sm" variant="outline-info" onClick={() => setShowResetPasswordModal(true)} className="rounded-pill px-3"><Key className="me-1" />MDP</Button>
                          </ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 bg-white">
                      <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                        <Card.Title className="mb-0 fs-5 fw-bold text-secondary">
                          <Building className="me-2" />Métadonnées Admin
                        </Card.Title>
                      </Card.Header>
                      <Card.Body className="p-4">
                        <div className="mb-3">
                          <h6 className="text-muted mb-3 text-uppercase small fw-bold">Informations techniques</h6>
                          <div className="bg-light p-3 rounded-3">
                            <p className="mb-2 small"><strong>ID Utilisateur:</strong> {adminUser?.user_id || adminUser?.id || entite?.admin_user_id || 'N/A'}</p>
                            <p className="mb-0 small"><strong>Ajouté par:</strong> {adminUser?.added_by || "Système"}</p>
                          </div>
                        </div>
                        <div>
                          <h6 className="text-muted mb-3 text-uppercase small fw-bold">Dates importantes</h6>
                          <div className="bg-light p-3 rounded-3">
                            <p className="mb-2 small"><strong><Calendar className="me-2" size={12} /> Création:</strong> {formatDateSimple(adminUser?.created_at || entite?.created_at)}</p>
                            <p className="mb-0 small"><strong>Modification:</strong> {formatDateSimple(adminUser?.updated_at || entite?.updated_at)}</p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Card className="border-0 shadow-sm rounded-4 mt-2">
                  <Card.Body className="text-center py-5">
                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '80px', height: '80px' }}>
                      <PersonCheck size={40} className="text-muted" />
                    </div>
                    <h5 className="fw-bold mb-2">Aucun administrateur associé</h5>
                    <p className="text-muted mb-0">Cette entité n'a pas encore d'administrateur assigné.</p>
                  </Card.Body>
                </Card>
              )}
            </Tab>

            {/* Onglet Tous les Admins - UNIQUEMENT 1002 et 1003 */}
            <Tab 
              eventKey="allAdmins" 
              title={
                <span>
                  <People className="me-2" />
                  Admins Entité/Principal
                  <Badge bg="info" className="ms-2 rounded-pill">
                    {filteredAdmins.length}
                  </Badge>
                </span>
              }
            >
              <Row className="g-4 mt-2">
                <Col lg={12}>
                  <Card className="border-0 shadow-sm rounded-4 bg-white">
                    <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
                      <Card.Title className="mb-0 fs-5 fw-bold text-info">
                        <People className="me-2" />
                        Administrateurs de l'entité
                      </Card.Title>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={() => {
                          setNewAdminData({
                            admin_lname: '',
                            admin_fname: '',
                            admin_phone: '',
                            admin_fonction: '',
                            email: '',
                            admin_type: 'admin_entite',
                            matricule_interne: '',
                            code_agent_interne: '',
                            id_marche: null
                          });
                          setShowAddAdminModal(true);
                        }}
                        className="rounded-pill"
                      >
                        <Plus className="me-1" size={16} /> Ajouter un admin
                      </Button>
                    </Card.Header>
                    <Card.Body className="p-4">
                      {loadingAdmins ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="info" />
                          <p className="mt-2 text-muted">Chargement des administrateurs...</p>
                        </div>
                      ) : filteredAdmins.length === 0 ? (
                        <Alert variant="info" className="text-center">
                          <InfoCircle className="me-2" />
                          Aucun administrateur avec le rôle Admin Entité (1002) ou Admin Principal (1003) trouvé.
                        </Alert>
                      ) : (
                        <div className="table-responsive">
                          <Table hover className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Nom complet</th>
                                <th>Rôle</th>
                                <th>Email</th>
                                <th>Téléphone</th>
                                <th>Fonction</th>
                                <th>Statut</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredAdmins.map(admin => {
                                const roleId = admin.role_id || admin.role?.role_id || null;
                                const roleLabel = roleId === 1002 ? 'Admin Entité' : 
                                                 roleId === 1003 ? 'Admin Principal' : 
                                                 'Utilisateur';
                                const roleColor = roleId === 1002 ? 'primary' : 
                                                 roleId === 1003 ? 'danger' : 
                                                 'secondary';
                                
                                return (
                                  <tr key={admin.user_id || admin.id}>
                                    <td>
                                      <strong>
                                        {admin.admin_lname || admin.last_name} {admin.admin_fname || admin.first_name}
                                      </strong>
                                      {(adminUser && (admin.user_id === adminUser.user_id || admin.id === adminUser.id)) && (
                                        <Badge bg="primary" className="ms-2">Principal</Badge>
                                      )}
                                    </td>
                                    <td>
                                      <Badge bg={roleColor} className="rounded-pill">
                                        {roleLabel}
                                      </Badge>
                                      {roleId === 1002 && (
                                        <Badge bg="secondary" className="ms-1 rounded-pill">
                                          <Building className="me-1" size={10} />
                                          Hors Marché
                                        </Badge>
                                      )}
                                      {roleId === 1003 && (
                                        <Badge bg="info" className="ms-1 rounded-pill">
                                          <Globe className="me-1" size={10} />
                                          Marchés
                                        </Badge>
                                      )}
                                    </td>
                                    <td>{admin.email}</td>
                                    <td>{admin.admin_phone || admin.phone || '-'}</td>
                                    <td>{admin.admin_fonction || admin.fonction || '-'}</td>
                                    <td>{getUserStatusBadge(admin.is_active)}</td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <Button 
                                          size="sm" 
                                          variant="outline-warning" 
                                          onClick={() => {
                                            setSelectedAdmin(admin);
                                            setShowResetPasswordModal(true);
                                          }}
                                          className="rounded-pill"
                                        >
                                          <Key size={14} /> MDP
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant={admin.is_active === 1 ? "outline-danger" : "outline-success"}
                                          onClick={() => {
                                            setSelectedAdmin(admin);
                                            setShowStatusModal(true);
                                          }}
                                          className="rounded-pill"
                                        >
                                          {admin.is_active === 1 ? <Lock size={14} /> : <Unlock size={14} />}
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </div>

        {/* ============================================================ */}
        {/* MODAL AJOUT D'ADMIN (1002 ou 1003) */}
        {/* ============================================================ */}
        <Modal show={showAddAdminModal} onHide={() => setShowAddAdminModal(false)} size="lg" centered backdrop="static" className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4 bg-primary text-white rounded-top-0">
            <Modal.Title className="fw-bold">
              <PersonPlus className="me-2" size={24} />
              Ajouter un administrateur à l'entité
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {/* Type d'admin */}
            <div className="mb-4">
              <h6 className="text-muted mb-3 border-bottom pb-2">
                <Shield className="me-2" size={16} /> Type d'administrateur
              </h6>
              <Row className="g-3">
                {['admin_entite', 'admin_principal'].map((typeKey) => {
                  const config = adminTypeConfig[typeKey];
                  const Icon = config.icon;
                  const isSelected = newAdminData.admin_type === typeKey;
                  return (
                    <Col key={typeKey} md={6}>
                      <div 
                        className={`p-3 rounded-3 h-100 w-100 transition-all ${isSelected ? 'shadow-lg' : 'shadow-sm'}`}
                        style={{
                          backgroundColor: isSelected ? config.bgLight : 'white',
                          border: isSelected ? `2px solid ${config.color}` : '2px solid #e9ecef',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                          position: 'relative'
                        }}
                        onClick={() => setNewAdminData({...newAdminData, admin_type: typeKey})}
                      >
                        {isSelected && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <CheckCircle size={20} color={config.color} />
                          </div>
                        )}
                        <div className="d-flex align-items-start gap-3">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: '48px',
                              height: '48px',
                              backgroundColor: isSelected ? config.color : config.bgLight,
                              color: isSelected ? 'white' : config.color,
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <Icon size={24} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-semibold" style={{ fontSize: '15px' }}>
                                {config.label}
                              </span>
                              <Badge bg={isSelected ? config.color : 'primary'} className="rounded-pill" style={{ fontSize: '9px' }}>
                                {config.badge}
                              </Badge>
                            </div>
                            <p className="text-muted small mb-0 mt-1" style={{ fontSize: '12px' }}>
                              {config.description}
                            </p>
                            <small className="text-muted d-block mt-1" style={{ fontSize: '10px' }}>
                              <Shield size={10} className="me-1" />
                              Rôle ID: {config.roleId}
                            </small>
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>

            {/* Informations personnelles */}
            <div className="mb-4">
              <h6 className="text-muted mb-3 border-bottom pb-2">
                <Person className="me-2" size={16} /> Informations personnelles
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAdminData.admin_lname} 
                      onChange={e => setNewAdminData({...newAdminData, admin_lname: e.target.value})} 
                      placeholder="Nom de famille" 
                      className="rounded-3"
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAdminData.admin_fname} 
                      onChange={e => setNewAdminData({...newAdminData, admin_fname: e.target.value})} 
                      placeholder="Prénom" 
                      className="rounded-3"
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold"><Phone className="me-1" size={14} /> Téléphone <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="tel" 
                      value={newAdminData.admin_phone} 
                      onChange={e => {
                        const onlyNumbers = e.target.value.replace(/\D/g, '');
                        setNewAdminData({...newAdminData, admin_phone: onlyNumbers});
                      }} 
                      placeholder="XXX XXX XXXX" 
                      className="rounded-3"
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold"><Envelope className="me-1" size={14} /> Email</Form.Label>
                    <Form.Control 
                      type="email" 
                      value={newAdminData.email} 
                      onChange={e => setNewAdminData({...newAdminData, email: e.target.value})} 
                      placeholder="exemple@email.com" 
                      className="rounded-3"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold"><Briefcase className="me-1" size={14} /> Fonction <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAdminData.admin_fonction} 
                      onChange={e => setNewAdminData({...newAdminData, admin_fonction: e.target.value})} 
                      placeholder="Fonction" 
                      className="rounded-3"
                      required 
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Identifiants internes (caché) */}
            <div className="mb-3 d-none">
              <h6 className="text-muted mb-3 border-bottom pb-2">
                <Key className="me-2" size={16} /> Identifiants Internes
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Matricule Interne</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAdminData.matricule_interne} 
                      onChange={e => setNewAdminData({...newAdminData, matricule_interne: e.target.value})} 
                      placeholder="Ex: MAT-2024-001"
                      className="rounded-3"
                    />
                    <Form.Text className="text-muted">
                      Identifiant unique interne de l'admin
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Code Agent Interne</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAdminData.code_agent_interne} 
                      onChange={e => setNewAdminData({...newAdminData, code_agent_interne: e.target.value})} 
                      placeholder="Ex: AG-2024-001"
                      className="rounded-3"
                    />
                    <Form.Text className="text-muted">
                      Code unique pour identification interne
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2">
            <Button 
              variant="light" 
              onClick={() => setShowAddAdminModal(false)} 
              className="rounded-pill px-4"
              disabled={savingNewAdmin}
            >
              <X className="me-2" size={14} /> Annuler
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveNewAdmin} 
              disabled={
                savingNewAdmin ||
                !newAdminData.admin_lname.trim() ||
                !newAdminData.admin_fname.trim() ||
                !newAdminData.admin_phone.trim() ||
                !newAdminData.admin_fonction.trim()
              }
              className="rounded-pill px-4"
            >
              {savingNewAdmin ? (
                <><Spinner animation="border" size="sm" className="me-2" />Enregistrement...</>
              ) : (
                <><Save className="me-2" size={16} /> Enregistrer l'admin</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ============================================================ */}
        {/* MODALE DES IDENTIFIANTS DU NOUVEL ADMIN */}
        {/* ============================================================ */}
        <Modal 
          show={showCredentialsModal} 
          onHide={() => {
            setShowCredentialsModal(false);
            setNewAdminCredentials(null);
          }} 
          centered 
          size="lg" 
          className="rounded-4"
          backdrop="static"
        >
          <Modal.Header className="border-0 pt-4 px-4 bg-success text-white rounded-top-0">
            <Modal.Title className="fw-bold d-flex align-items-center">
              <CheckCircle className="me-2" size={28} />
              Admin créé avec succès ! 
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="p-4">
            {newAdminCredentials && (
              <>
                {/* Badge de type */}
                <div className="text-center mb-4">
                  <Badge 
                    bg={newAdminCredentials.admin_type === 'admin_entite' ? 'primary' : 'danger'} 
                    className="px-4 py-2 rounded-pill fs-6"
                  >
                    {newAdminCredentials.admin_type === 'admin_entite' ? 'Admin Entité' : 'Admin Principal'}
                  </Badge>
                </div>

                {/* Informations de l'admin */}
                <Card className="border-0 shadow-sm rounded-3 bg-light">
                  <Card.Body>
                    <div className="table-responsive">
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td className="fw-bold text-muted" style={{ width: '40%' }}>
                              <Person className="me-2 text-primary" size={18} />
                              Nom complet :
                            </td>
                            <td className="bg-white p-2 rounded-3">
                              <strong>{newAdminCredentials.admin_fname} {newAdminCredentials.admin_lname}</strong>
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-muted">
                              <Envelope className="me-2 text-primary" size={18} />
                              Email :
                            </td>
                            <td className="bg-white p-2 rounded-3">{newAdminCredentials.email}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-muted">
                              <Phone className="me-2 text-primary" size={18} />
                              Téléphone :
                            </td>
                            <td className="bg-white p-2 rounded-3">{newAdminCredentials.admin_phone}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-muted">
                              <Shield className="me-2 text-info" size={18} />
                              Type :
                            </td>
                            <td className="bg-white p-2 rounded-3">
                              <Badge bg={newAdminCredentials.admin_type === 'admin_entite' ? 'primary' : 'danger'}>
                                {newAdminCredentials.admin_type === 'admin_entite' ? 'Admin Entité (ID: 1002)' : 'Admin Principal (ID: 1003)'}
                              </Badge>
                            </td>
                          </tr>
                          {newAdminCredentials.matricule_interne && (
                            <tr>
                              <td className="fw-bold text-muted">
                                <Key className="me-2 text-warning" size={18} />
                                Matricule interne :
                              </td>
                              <td className="bg-white p-2 rounded-3">{newAdminCredentials.matricule_interne}</td>
                            </tr>
                          )}
                          {newAdminCredentials.code_agent_interne && (
                            <tr>
                              <td className="fw-bold text-muted">
                                <Key className="me-2 text-warning" size={18} />
                                Code agent interne :
                              </td>
                              <td className="bg-white p-2 rounded-3">{newAdminCredentials.code_agent_interne}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>

                {/* Nouveau mot de passe */}
                <div className="mt-4">
                  <div className="bg-warning bg-opacity-10 p-4 rounded-3 border border-warning">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div>
                        <small className="text-muted text-uppercase fw-semibold">Nouveau mot de passe temporaire</small>
                        <h4 className="text-warning fw-bold mb-0 mt-1" style={{ fontFamily: 'monospace', letterSpacing: '4px' }}>
                          {newAdminCredentials.password_temp}
                        </h4>
                      </div>
                      <Button 
                        variant="outline-warning" 
                        onClick={() => {
                          navigator.clipboard.writeText(newAdminCredentials.password_temp);
                          showNotification('Mot de passe copié ! ✅', 'success');
                        }}
                        className="rounded-pill"
                      >
                        <Copy className="me-1" size={16} />
                        Copier
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Message important */}
                <Alert variant="info" className="mt-4 mb-0">
                  <InfoCircle className="me-2" />
                  <strong>Information importante :</strong><br />
                  Ce mot de passe est temporaire. L'utilisateur devra le changer lors de sa première connexion.
                  <hr className="my-2" />
                  <small className="text-muted">
                    💡 Communiquez ces identifiants à l'administrateur pour qu'il puisse se connecter.
                  </small>
                </Alert>
              </>
            )}
          </Modal.Body>
          
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2 flex-wrap">
            <Button 
              variant="outline-primary" 
              onClick={() => newAdminCredentials && copyAdminCredentialsMessage(newAdminCredentials)}
              className="rounded-pill px-4"
            >
              <FileText className="me-2" size={16} />
              Copier le message complet
            </Button>
            <Button 
              variant="outline-success" 
              onClick={() => {
                navigator.clipboard.writeText(newAdminCredentials?.password_temp || '');
                showNotification('Mot de passe copié ! ✅', 'success');
              }}
              className="rounded-pill px-4"
            >
              <Copy className="me-2" size={16} />
              Copier le mot de passe
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                setShowCredentialsModal(false);
                setNewAdminCredentials(null);
                // Rafraîchir les données
                fetchEntiteAdmins();
                fetchEntiteDetails();
              }}
              className="rounded-pill px-4"
            >
              <Check className="me-2" size={16} />
              Terminer
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal pour éditer un admin spécifique */}
        <Modal show={showAdminEditModal} onHide={() => { setShowAdminEditModal(false); setSelectedAdmin(null); }} centered size="lg" className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4 bg-primary text-white">
            <Modal.Title className="fw-bold">
              <Pencil className="me-2" />Modifier l'administrateur
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    value={adminData.admin_lname} 
                    onChange={e => setAdminData({...adminData, admin_lname: e.target.value})} 
                    required 
                    className="rounded-3" 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    value={adminData.admin_fname} 
                    onChange={e => setAdminData({...adminData, admin_fname: e.target.value})} 
                    required 
                    className="rounded-3" 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold"><Envelope className="me-1" size={14} /> Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="email" 
                    value={adminData.email} 
                    onChange={e => setAdminData({...adminData, email: e.target.value})} 
                    required 
                    className="rounded-3" 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold"><Phone className="me-1" size={14} /> Téléphone</Form.Label>
                  <Form.Control 
                    type="tel" 
                    value={adminData.admin_phone} 
                    onChange={e => setAdminData({...adminData, admin_phone: e.target.value})} 
                    className="rounded-3" 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold"><Briefcase className="me-1" size={14} /> Fonction</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={adminData.admin_fonction} 
                    onChange={e => setAdminData({...adminData, admin_fonction: e.target.value})} 
                    className="rounded-3" 
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold"><Shield className="me-1" size={14} /> Statut</Form.Label>
                  <Form.Select 
                    value={adminData.is_active} 
                    onChange={e => setAdminData({...adminData, is_active: parseInt(e.target.value)})} 
                    className="rounded-3"
                  >
                    <option value={1}>Actif</option>
                    <option value={0}>Inactif</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2">
            <Button variant="light" onClick={() => { setShowAdminEditModal(false); setSelectedAdmin(null); }} className="rounded-pill px-4">
              Annuler
            </Button>
            <Button variant="primary" onClick={handleUpdateSpecificAdmin} disabled={saving} className="rounded-pill px-4">
              {saving ? <Spinner size="sm" className="me-2" /> : <><Save className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de statut (réutilisé pour admin principal et spécifique) */}
        <Modal show={showStatusModal} onHide={() => { setShowStatusModal(false); setSelectedAdmin(null); }} centered className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4">
            <Modal.Title className="fw-bold">
              {selectedAdmin ? (
                selectedAdmin.is_active === 1 ? <><Lock className="me-2 text-warning" />Désactiver</> : <><Unlock className="me-2 text-success" />Activer</>
              ) : (
                adminUser?.is_active === 1 ? <><Lock className="me-2 text-warning" />Désactiver</> : <><Unlock className="me-2 text-success" />Activer</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center px-4 pb-2">
            <div className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 ${(selectedAdmin || adminUser)?.is_active === 1 ? 'bg-warning' : 'bg-success'}`} style={{ width: '64px', height: '64px' }}>
              {(selectedAdmin || adminUser)?.is_active === 1 ? <Lock size={32} className="text-white" /> : <Unlock size={32} className="text-white" />}
            </div>
            <h5 className="fw-bold mb-2">
              {(selectedAdmin || adminUser)?.is_active === 1 ? "Désactiver cet administrateur ?" : "Activer cet administrateur ?"}
            </h5>
            <p className="text-muted">
              {(selectedAdmin || adminUser)?.is_active === 1 ? "L'administrateur ne pourra plus se connecter à la plateforme." : "L'administrateur pourra à nouveau se connecter à la plateforme."}
            </p>
            {selectedAdmin && (
              <Alert variant="info" className="mt-3">
                <InfoCircle className="me-2" />
                Admin: <strong>{selectedAdmin.admin_lname || selectedAdmin.last_name} {selectedAdmin.admin_fname || selectedAdmin.first_name}</strong>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2">
            <Button variant="light" onClick={() => { setShowStatusModal(false); setSelectedAdmin(null); }} className="rounded-pill px-4">
              Annuler
            </Button>
            <Button 
              variant={(selectedAdmin || adminUser)?.is_active === 1 ? "warning" : "success"} 
              onClick={selectedAdmin ? handleToggleSpecificAdminStatus : handleToggleAdminStatus} 
              disabled={togglingStatus} 
              className="rounded-pill px-4"
            >
              {togglingStatus ? <Spinner size="sm" className="me-2" /> : ((selectedAdmin || adminUser)?.is_active === 1 ? "Désactiver" : "Activer")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de réinitialisation de mot de passe (réutilisé) */}
        <Modal show={showResetPasswordModal} onHide={() => { setShowResetPasswordModal(false); setSelectedAdmin(null); }} centered className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4">
            <Modal.Title className="fw-bold"><Key className="me-2 text-info" />Réinitialiser le mot de passe</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center px-4 pb-2">
            <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '64px', height: '64px' }}>
              <Key size={32} className="text-info" />
            </div>
            <h5 className="fw-bold mb-2">Confirmer la réinitialisation</h5>
            <p className="text-muted">Un nouveau mot de passe temporaire sera généré pour l'administrateur:</p>
            {selectedAdmin && (
              <Alert variant="info" className="mt-2">
                <strong>{selectedAdmin.admin_lname || selectedAdmin.last_name} {selectedAdmin.admin_fname || selectedAdmin.first_name}</strong><br />
                <small>{selectedAdmin.email}</small>
              </Alert>
            )}
            <p className="text-muted mt-3">Le mot de passe sera affiché après confirmation.</p>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2">
            <Button variant="light" onClick={() => { setShowResetPasswordModal(false); setSelectedAdmin(null); }} className="rounded-pill px-4">
              Annuler
            </Button>
            <Button 
              variant="info" 
              onClick={selectedAdmin ? handleResetSpecificAdminPassword : handleResetPassword} 
              disabled={resettingPassword}
              className="rounded-pill px-4 text-white"
            >
              {resettingPassword ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Réinitialisation...
                </>
              ) : (
                'Réinitialiser'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal retirer rôle */}
        <Modal show={showRemoveRoleModal} onHide={() => setShowRemoveRoleModal(false)} centered className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4 bg-danger text-white rounded-top-4">
            <Modal.Title className="fw-bold"><Trash className="me-2" />Retirer le rôle</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center px-4 pb-2 pt-4">
            <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: '64px', height: '64px' }}>
              <ExclamationTriangle size={32} className="text-danger" />
            </div>
            <h5 className="fw-bold mb-2">Confirmer le retrait du rôle</h5>
            <p className="text-muted">L'utilisateur perdra toutes les permissions associées à ce rôle.</p>
            {roleToRemove && (
              <Alert variant="danger" className="mt-3 mb-0 py-2 rounded-3">
                <strong>{roleToRemove.name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2">
            <Button variant="light" onClick={() => setShowRemoveRoleModal(false)} className="rounded-pill px-4">Annuler</Button>
            <Button variant="danger" onClick={handleRemoveRole} disabled={removingRole} className="rounded-pill px-4">
              {removingRole ? <Spinner size="sm" className="me-2" /> : "Retirer"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal permissions */}
        <Modal show={showPermissionsModal} onHide={() => { setShowPermissionsModal(false); setSelectedAdmin(null); }} size="lg" centered className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4 bg-primary text-white rounded-top-4">
            <Modal.Title className="fw-bold">
              <Shield className="me-2" />Permissions ({permissions.length})
              {selectedAdmin && (
                <small className="ms-2 text-white-50">
                  - {selectedAdmin.admin_lname || selectedAdmin.last_name} {selectedAdmin.admin_fname || selectedAdmin.first_name}
                </small>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }} className="p-4">
            {loadingPermissions ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : permissions.length === 0 ? (
              <div className="text-center py-5">
                <Shield size={48} className="text-muted mb-3" />
                <p className="text-muted">Aucune permission disponible</p>
              </div>
            ) : (
              Object.entries(groupedPermissions).map(([group, perms]) => (
                <Card key={group} className="mb-3 border rounded-3 overflow-hidden">
                  <Card.Header className="bg-light py-3" style={{ cursor: 'pointer' }} onClick={() => toggleGroup(group)}>
                    <div className="d-flex align-items-center">
                      {expandedGroups[group] ? <ChevronDown className="me-2" /> : <ChevronRight className="me-2" />}
                      <strong className="fs-6">{formatGroupName(group)}</strong>
                      <Badge bg="primary" className="ms-2 rounded-pill">{perms.length}</Badge>
                    </div>
                  </Card.Header>
                  {expandedGroups[group] && (
                    <Card.Body>
                      <Row>
                        {perms.map(p => (
                          <Col md={6} key={p.id} className="mb-3">
                            <Card className="border h-100 shadow-sm rounded-3">
                              <Card.Body className="py-3">
                                <div className="d-flex align-items-start">
                                  <Shield className="text-primary me-2 mt-1 flex-shrink-0" size={16} />
                                  <div>
                                    <strong className="d-block mb-1">{p.display_name}</strong>
                                    <p className="text-muted small mb-2">{p.description || 'Aucune description'}</p>
                                    <div className="d-flex gap-2">
                                      <Badge bg="info" className="rounded-pill px-2 py-1">{p.action}</Badge>
                                      <Badge bg={getScopeBadge(p.scope)} className="rounded-pill px-2 py-1">{p.scope}</Badge>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  )}
                </Card>
              ))
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4">
            <Button variant="light" onClick={() => { setShowPermissionsModal(false); setSelectedAdmin(null); }} className="rounded-pill px-4">Fermer</Button>
            {permissions.length > 0 && (
              <Button variant="primary" onClick={() => exportToCSV(permissions.map(p => ({ Permission: p.display_name, Description: p.description, Action: p.action, Scope: p.scope })), `permissions_${entite?.entite_id}`)} className="rounded-pill px-4">
                <Download className="me-2" />Exporter
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Modal mot de passe réinitialisé - Version avec statut SMS */}
        <Modal show={passwordResetData.show} onHide={() => setPasswordResetData({ ...passwordResetData, show: false })} centered size="lg" className="rounded-4">
          <Modal.Header closeButton className="border-0 pt-4 px-4 bg-success text-white">
            <Modal.Title className="fw-bold">
              <Key className="me-2" />Mot de passe réinitialisé
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 pb-2 pt-4">
            {/* Informations utilisateur */}
            <div className="mb-4">
              <div className="bg-light p-3 rounded-3 text-center">
                <small className="text-muted text-uppercase">Utilisateur</small>
                <h5 className="mb-0">{passwordResetData.username}</h5>
                {passwordResetData.phone && (
                  <small className="text-muted d-block mt-1">
                    📱 {passwordResetData.phone}
                  </small>
                )}
              </div>
            </div>
            
            {/* Nouveau mot de passe */}
            <div className="text-center mb-4">
              <div className="bg-warning bg-opacity-10 p-4 rounded-3 border border-warning">
                <small className="text-muted text-uppercase">Nouveau mot de passe temporaire</small>
                <div className="d-flex align-items-center justify-content-center gap-3 mt-2 flex-wrap">
                  <h1 className="text-warning fw-bold mb-0" style={{ fontFamily: 'monospace', letterSpacing: '4px' }}>
                    {passwordResetData.password}
                  </h1>
                  <Button 
                    variant="outline-warning" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(passwordResetData.password);
                      showNotification('Mot de passe copié dans le presse-papier', 'success');
                    }}
                    title="Copier le mot de passe"
                  >
                    <Copy className="me-1" size={14} />
                    Copier
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Statut de l'envoi SMS */}
            <div className="mb-4">
              <h6 className="fw-semibold mb-2">
                <Shield className="me-2 text-primary" size={16} />
                Statut de l'envoi SMS
              </h6>
              
              {/* Message de succès SMS */}
              {passwordResetData.sms_sent === true && (
                <Alert variant="success" className="mb-2">
                  <div className="d-flex align-items-start">
                    <CheckCircle className="me-2 mt-1" size={18} />
                    <div>
                      <strong>SMS envoyé avec succès !</strong>
                      <br />
                      <small>
                        Un SMS contenant le nouveau mot de passe a été envoyé à {passwordResetData.phone || 'l\'utilisateur'}
                      </small>
                      {passwordResetData.sms_response && (
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted">
                            <strong>Réponse du serveur SMS :</strong><br />
                            ID: {passwordResetData.sms_response.sms_id || '-'}<br />
                            Statut: {passwordResetData.sms_response.message || 'ENVOYÉ'}<br />
                            Fournisseur: {passwordResetData.sms_response.provider_sms || '-'}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </Alert>
              )}
              
              {/* Message d'erreur SMS */}
              {passwordResetData.sms_sent === false && (
                <Alert variant="warning" className="mb-2">
                  <div className="d-flex align-items-start">
                    <ExclamationCircle className="me-2 mt-1" size={18} />
                    <div>
                      <strong>Attention - SMS non envoyé</strong>
                      <br />
                      <small>
                        {passwordResetData.sms_error || 'Erreur lors de l\'envoi du SMS. Vérifiez le numéro de téléphone.'}
                      </small>
                      <div className="mt-2 pt-2 border-top">
                        <small className="text-muted">
                          <strong>Le mot de passe a bien été réinitialisé mais n'a pas pu être envoyé par SMS.</strong><br />
                          Veuillez communiquer le mot de passe manuellement à l'utilisateur.
                        </small>
                      </div>
                    </div>
                  </div>
                </Alert>
              )}
              
              {/* Aucune information SMS */}
              {passwordResetData.sms_sent === undefined && (
                <Alert variant="info" className="mb-2">
                  <div className="d-flex align-items-start">
                    <InfoCircle className="me-2 mt-1" size={18} />
                    <div>
                      <strong>Informations SMS</strong>
                      <br />
                      <small>Le mot de passe a été réinitialisé. Assurez-vous de le communiquer à l'utilisateur.</small>
                    </div>
                  </div>
                </Alert>
              )}
            </div>
            
            {/* Message important */}
            <Alert variant="info" className="mt-2 text-start">
              <InfoCircle className="me-2" />
              <strong>Information importante :</strong><br />
              Ce mot de passe est temporaire. L'utilisateur devra le changer lors de sa prochaine connexion.
              <hr className="my-2" />
              <small>ID: {passwordResetData.user_id}</small>
            </Alert>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-2">
            <Button 
              variant="success" 
              onClick={() => {
                navigator.clipboard.writeText(passwordResetData.password);
                showNotification('Mot de passe copié dans le presse-papier', 'success');
              }}
              className="rounded-pill px-4"
            >
              <Copy className="me-2" />Copier le mot de passe
            </Button>
            <Button 
              variant="primary" 
              onClick={() => setPasswordResetData({ ...passwordResetData, show: false })}
              className="rounded-pill px-4"
            >
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>

      <style jsx>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
        }
        .rounded-4 {
          border-radius: 1rem !important;
        }
        .rounded-3 {
          border-radius: 0.75rem !important;
        }
        .btn-rounded {
          border-radius: 2rem !important;
        }
        .transition-all {
          transition: all 0.3s ease !important;
        }
      `}</style>
    </React.Fragment>
  );
}